import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, uploadCsv } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Upload, Search, ExternalLink, Sparkles } from "lucide-react";
import { TierBadge, StatusPill, ScoreBar, STATUS_LABELS } from "@/components/Bits";

const CATEGORIES = ["hospitality", "events", "retail", "services", "charity", "community", "other"];
const SIZES = ["small", "medium", "large"];

function AddLeadDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    business_name: "", website: "", contact_email: "", contact_name: "",
    phone: "", location: "", category: "", business_size: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      const res = await api.post("/leads", payload);
      toast.success(`Added ${res.data.business_name}`);
      setOpen(false);
      setForm({ business_name: "", website: "", contact_email: "", contact_name: "", phone: "", location: "", category: "", business_size: "", notes: "" });
      onCreated && onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add lead");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white" data-testid="add-lead-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add Lead
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Add a lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Business name *</Label>
            <Input required value={form.business_name} onChange={(e) => upd("business_name", e.target.value)} data-testid="input-business-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Website</Label>
              <Input value={form.website} onChange={(e) => upd("website", e.target.value)} placeholder="example.co.uk" data-testid="input-website" />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => upd("location", e.target.value)} placeholder="Paisley" data-testid="input-location" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Contact name</Label>
              <Input value={form.contact_name} onChange={(e) => upd("contact_name", e.target.value)} data-testid="input-contact-name" />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} data-testid="input-contact-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger data-testid="select-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Business size</Label>
              <Select value={form.business_size} onValueChange={(v) => upd("business_size", v)}>
                <SelectTrigger data-testid="select-size"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{SIZES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="input-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm" data-testid="submit-add-lead">
              {saving ? "Adding…" : "Add lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const doUpload = async () => {
    const f = inputRef.current?.files?.[0];
    if (!f) return toast.error("Choose a CSV first");
    setBusy(true);
    try {
      const res = await uploadCsv("/leads/import-csv", f);
      setResult(res.data);
      toast.success(`Imported ${res.data.created} new leads`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm" data-testid="import-csv-button">
          <Upload className="h-4 w-4 mr-1.5" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="import-csv-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Import leads from CSV</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-gray-600 space-y-2">
          <p>Expected columns (any subset): <code className="font-mono">business_name, website, contact_email, contact_name, phone, location, category, business_size, notes, facebook, instagram, twitter, linkedin</code></p>
        </div>
        <Input type="file" accept=".csv,text/csv" ref={inputRef} data-testid="csv-file-input" />
        {result && (
          <div className="text-xs bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-1" data-testid="csv-import-result">
            <div>Created: <span className="font-mono">{result.created}</span></div>
            <div>Duplicates skipped: <span className="font-mono">{result.duplicates}</span></div>
            <div>Suppressed skipped: <span className="font-mono">{result.suppressed}</span></div>
            {result.errors?.length > 0 && <div>Errors: <span className="font-mono">{result.errors.length}</span></div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Close</Button>
          <Button onClick={doUpload} disabled={busy} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm" data-testid="submit-import-csv">
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Leads() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [tier, setTier] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (category) params.set("category", category);
      if (tier) params.set("suggested_tier", tier);
      const res = await api.get("/leads?" + params.toString());
      setItems(res.data.items);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="leads-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="uppercase-tiny text-gray-500">Prospecting</div>
          <h1 className="font-heading text-4xl tracking-tight mt-1">Leads</h1>
          <p className="text-sm text-gray-600 mt-2">All businesses considered for a paisley.org.uk partnership.</p>
        </div>
        <div className="flex gap-2">
          <ImportCsvDialog onDone={load} />
          <AddLeadDialog onCreated={load} />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="leads-filters">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search business, email, website…" className="pl-8 rounded-sm" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="filter-search" />
        </div>
        <Select value={status || "any"} onValueChange={(v) => setStatus(v === "any" ? "" : v)}>
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category || "any"} onValueChange={(v) => setCategory(v === "any" ? "" : v)}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any category</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tier || "any"} onValueChange={(v) => setTier(v === "any" ? "" : v)}>
          <SelectTrigger data-testid="filter-tier"><SelectValue placeholder="Suggested tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any tier</SelectItem>
            <SelectItem value="bronze">Bronze</SelectItem>
            <SelectItem value="silver">Silver</SelectItem>
            <SelectItem value="gold">Gold</SelectItem>
            <SelectItem value="platinum">Platinum</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white" data-testid="apply-filters-button">Apply</Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left uppercase-tiny text-gray-600">
              <th className="px-4 py-3 font-medium">Business</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Location</th>
              <th className="px-4 py-3 font-medium">Fit score</th>
              <th className="px-4 py-3 font-medium">Tier</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Contact</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-gray-500" data-testid="leads-empty-state">
                <Sparkles className="h-6 w-6 mx-auto mb-3 text-gray-400" />
                No leads yet. Add one or import a CSV to get started.
              </td></tr>
            )}
            {items.map((l) => (
              <tr key={l.lead_id} className="border-b border-gray-100 hover:bg-gray-50/50" data-testid={`lead-row-${l.lead_id}`}>
                <td className="px-4 py-3">
                  <Link to={`/leads/${l.lead_id}`} className="font-medium text-gray-900 hover:text-[#1B4332]" data-testid={`lead-link-${l.lead_id}`}>
                    {l.business_name}
                  </Link>
                  {l.website && (
                    <a href={l.website} target="_blank" rel="noreferrer" className="ml-1 inline-flex text-gray-400 hover:text-[#1B4332]">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-700 capitalize">{l.category || "—"}</td>
                <td className="px-4 py-3 text-gray-700">{l.location || "—"}</td>
                <td className="px-4 py-3"><ScoreBar score={l.fit_score} /></td>
                <td className="px-4 py-3"><TierBadge tier={l.suggested_tier} /></td>
                <td className="px-4 py-3"><StatusPill status={l.status} /></td>
                <td className="px-4 py-3 text-gray-600 truncate max-w-[220px]">{l.contact_email || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
